import {
  cardWatchNoteOf,
  isCardWatchWorking,
  type ActionCardSurface,
} from '@littlebird/action-cards';
import { backendLabelOf } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui/layout/layout';
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/ui/popover/popover';
import { actionCardCommands } from '@/features/action-cards/commands';
import { IMPORTANCE_MAX } from '@/features/home/card-importance';
import { OpenWatcherButton } from '@/features/routines/components/internal-routines-section';
import type { KeyName } from '@/lib/key';
import { userStore } from '@/stores/user.store';
import { formatDateWithDay, formatTime } from '@/utils/date';

import { FeedRowAction } from './FeedRowActions';
import { snoozeOptionsAt, snoozeWhenLabel } from './feedSnooze';
import { useActionCardVote } from './useActionCardVote';
import {
  useCardPinAndSnooze,
  type CardPinAndSnooze,
} from './useCardPinAndSnooze';

import type { ActionCard, ActionCardWatch } from '@littlebird/sdk';

export type FeedRowAssign = (trigger: ReactElement) => ReactNode;

const OPTION_CLASS =
  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground outline-none transition-colors duration-[120ms] hover:bg-surface-hover focus-visible:bg-surface-hover motion-reduce:transition-none';

const OPTION_ICON_CLASS = 'size-3.5 shrink-0 text-foreground-subtle';

const DIVIDER_CLASS = 'mx-1.5 my-1 h-px bg-border';

const PIN_HOTKEY: readonly KeyName[] = ['p'];
const SNOOZE_HOTKEY: readonly KeyName[] = ['s'];

// The watcher checks every active card each run but writes to a card only when it finds something, so the card holds
// its last finding. A card from before the one watcher per user may still hold an old per-card watcher's status,
// which no longer means anything
const watchStatusLine = (watch: ActionCardWatch | null): string =>
  watch?.kept
    ? 'Kept by the user, so not watched'
    : 'Checked every 30 minutes with your other active cards';

// The card's ⋯ button, on the row's hover tray and the open card. A card that can still be acted on holds Pin, Assign and the
// snooze times; internal users also rate the card for BTS and see its importance, watcher findings and expiry; a
// dense open card ends with where it came from
export const FeedCardMoreMenu = observer(function FeedCardMoreMenu({
  card,
  actionable = true,
  assign,
  source,
  surface = 'web_feeds',
  cardExpanded = false,
}: {
  card: ActionCard;
  actionable?: boolean;
  assign?: FeedRowAssign;
  source?: ReactNode;
  surface?: ActionCardSurface;
  cardExpanded?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const internal = userStore.isInternalUser;
  if (!source && !actionable && !internal) return null;

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FeedRowAction
          icon={Icons.moreHorizontal}
          label="More"
          data-testid="feed-card-more"
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-testid="feed-card-more-content"
        className="w-64 p-1"
      >
        {actionable ? (
          <CardActions
            card={card}
            assign={assign}
            surface={surface}
            cardExpanded={cardExpanded}
            onDone={() => setOpen(false)}
          />
        ) : null}
        {actionable && internal ? <div className={DIVIDER_CLASS} /> : null}
        {internal ? (
          <InternalDetails card={card} onDone={() => setOpen(false)} />
        ) : null}
        {source && (actionable || internal) ? (
          <div className={DIVIDER_CLASS} />
        ) : null}
        {source ? (
          <div data-testid="feed-card-more-source">{source}</div>
        ) : null}
      </PopoverContent>
    </PopoverRoot>
  );
});

const CardActions = observer(function CardActions({
  card,
  assign,
  surface,
  cardExpanded,
  onDone,
}: {
  card: ActionCard;
  assign?: FeedRowAssign;
  surface: ActionCardSurface;
  cardExpanded: boolean;
  onDone: () => void;
}) {
  const { pinned, snoozed, togglePin, snooze, unsnooze } = useCardPinAndSnooze(
    card,
    surface,
    cardExpanded
  );

  return (
    <>
      <button
        type="button"
        className={OPTION_CLASS}
        aria-pressed={pinned}
        onClick={() => {
          void togglePin();
          onDone();
        }}
      >
        {pinned ? (
          <Icons.unpin aria-hidden="true" className={OPTION_ICON_CLASS} />
        ) : (
          <Icons.pin aria-hidden="true" className={OPTION_ICON_CLASS} />
        )}
        {pinned ? 'Unpin' : 'Pin to top'}
      </button>
      {/* Only a card the watcher moved to Working, which the user may not be working on */}
      {isCardWatchWorking(card) ? (
        <button
          type="button"
          className={OPTION_CLASS}
          onClick={() => {
            void actionCardCommands.notWorkingOnWatched(card.id);
            onDone();
          }}
        >
          <Icons.pause aria-hidden="true" className={OPTION_ICON_CLASS} />
          Not working on this
        </button>
      ) : null}
      {assign?.(
        <button type="button" className={OPTION_CLASS}>
          <Icons.userPlus aria-hidden="true" className={OPTION_ICON_CLASS} />
          Assign…
        </button>
      )}
      <div className="pt-1">
        <SnoozeOptions
          snoozed={snoozed}
          snooze={snooze}
          unsnooze={unsnooze}
          onDone={onDone}
        />
      </div>
    </>
  );
});

// "Snooze until" and its times, with Unsnooze on a card already snoozed
const SnoozeOptions = ({
  snoozed,
  snooze,
  unsnooze,
  onDone,
}: Pick<CardPinAndSnooze, 'snoozed' | 'snooze' | 'unsnooze'> & {
  onDone: () => void;
}) => {
  const now = new Date();
  return (
    <>
      <div className="flex items-center gap-2 px-2 pb-0.5 pt-1 text-xs font-medium text-foreground-subtle">
        <Icons.alarmClock aria-hidden="true" className="size-3 shrink-0" />
        Snooze until
      </div>
      {snoozed ? (
        <button
          type="button"
          className={OPTION_CLASS}
          onClick={() => {
            unsnooze();
            onDone();
          }}
        >
          <span className="flex-1 pl-5">Unsnooze</span>
        </button>
      ) : null}
      {snoozeOptionsAt(now).map((option) => (
        <button
          key={option.id}
          type="button"
          className={OPTION_CLASS}
          onClick={() => {
            snooze(option);
            onDone();
          }}
        >
          <span className="flex-1 pl-5">{option.label}</span>
          <span className="text-xs text-foreground-muted">
            {snoozeWhenLabel(option.until, now)}
          </span>
        </button>
      ))}
    </>
  );
};

// The collapsed row's own Snooze and Pin, before its ⋯ in the hover tray. The card host holds whether the snooze times
// are open, so S opens them too
export const FeedRowPinSnooze = observer(function FeedRowPinSnooze({
  card,
  surface = 'web_feeds',
  snoozeOpen,
  onSnoozeOpenChange,
}: {
  card: ActionCard;
  surface?: ActionCardSurface;
  snoozeOpen: boolean;
  onSnoozeOpenChange: (open: boolean) => void;
}) {
  const { pinned, snoozed, togglePin, snooze, unsnooze } = useCardPinAndSnooze(
    card,
    surface
  );

  return (
    <>
      <FeedRowAction
        icon={pinned ? Icons.unpin : Icons.pin}
        label={pinned ? 'Unpin' : 'Pin to top'}
        hotkey={PIN_HOTKEY}
        aria-pressed={pinned}
        onClick={() => void togglePin()}
      />
      <PopoverRoot open={snoozeOpen} onOpenChange={onSnoozeOpenChange}>
        <PopoverTrigger asChild>
          <FeedRowAction
            icon={Icons.alarmClock}
            label="Snooze"
            hotkey={SNOOZE_HOTKEY}
          />
        </PopoverTrigger>
        <PopoverContent
          align="end"
          data-testid="feed-row-snooze-content"
          className="w-56 p-1"
        >
          <SnoozeOptions
            snoozed={snoozed}
            snooze={snooze}
            unsnooze={unsnooze}
            onDone={() => onSnoozeOpenChange(false)}
          />
        </PopoverContent>
      </PopoverRoot>
    </>
  );
});

// Good or Bad opens the feedback dialog, which takes focus, so the menu closes first
const CardVote = ({
  cardId,
  onDone,
}: {
  cardId: string;
  onDone: () => void;
}) => {
  const { voted, rateGood, rateBad } = useActionCardVote(cardId);
  const options = [
    { choice: 'good', label: 'Good', icon: Icons.thumbsUp, rate: rateGood },
    { choice: 'bad', label: 'Bad', icon: Icons.thumbsDown, rate: rateBad },
  ] as const;
  return (
    <>
      {options.map(({ choice, label, icon: Icon, rate }) => (
        <button
          key={choice}
          type="button"
          className={OPTION_CLASS}
          aria-pressed={voted === choice}
          onClick={() => {
            onDone();
            rate();
          }}
        >
          <Icon aria-hidden="true" className={OPTION_ICON_CLASS} />
          <span className="flex-1">{label}</span>
          {voted === choice ? (
            <span className="text-xs text-foreground-muted">Sent</span>
          ) : null}
        </button>
      ))}
    </>
  );
};

const InternalDetails = observer(function InternalDetails({
  card,
  onDone,
}: {
  card: ActionCard;
  onDone: () => void;
}) {
  // The card's watch changes in place on the MobX card, so React Compiler must not cache it
  'use no memo';
  const watch = card.watch ?? null;
  const note = cardWatchNoteOf(card);

  return (
    <div className="p-2">
      <div className="flex items-center gap-2 px-2 pb-0.5">
        <span className="text-sm font-medium text-foreground">Rate card</span>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-foreground-subtle">
          Internal only
        </span>
      </div>
      <div className="-mx-2 mb-2 border-b border-border pb-2">
        <CardVote cardId={card.id} onDone={onDone} />
      </div>
      {card.backend ? (
        <>
          {/* Which backend made the card; it sat on every row before, the same word on each */}
          <span className="text-sm font-medium text-foreground">Backend</span>
          <YStack className="mb-3 gap-0.5 border-b border-border pb-3 pt-1.5">
            <span className="text-xs text-foreground-subtle">
              {backendLabelOf(card.backend)}
            </span>
          </YStack>
        </>
      ) : null}
      <span className="text-sm font-medium text-foreground">Importance</span>
      <YStack className="mb-3 gap-0.5 border-b border-border pb-3 pt-1.5">
        <span className="text-xs text-foreground-subtle">
          {card.importance == null
            ? 'Not rated'
            : `${card.importance} / ${IMPORTANCE_MAX}`}
        </span>
      </YStack>
      <span className="text-sm font-medium text-foreground">Watcher</span>
      <div className="flex flex-col gap-0.5 pt-1.5">
        <span className="text-xs text-foreground-subtle">
          {watchStatusLine(watch)}
        </span>
        {watch?.last_checked_at ? (
          <span className="text-xs text-foreground-muted">
            Last finding {formatTime(new Date(watch.last_checked_at))}
          </span>
        ) : (
          <span className="text-xs text-foreground-muted">
            The watcher has found nothing about this card yet.
          </span>
        )}
        {watch?.reason ? (
          <span className="text-xs text-foreground-muted">{watch.reason}</span>
        ) : null}
        {note ? (
          <span
            data-testid="feed-card-watch-note"
            className="text-xs text-foreground"
          >
            {note}
          </span>
        ) : null}
        <OpenWatcherButton routineId={watch?.routine_id} />
      </div>
      {card.expiresAt ? (
        <div className="mt-3 flex flex-col gap-0.5 border-t border-border pt-3">
          <span className="text-sm font-medium text-foreground">Expires</span>
          <span
            data-testid="feed-card-expires-at"
            className="text-xs text-foreground-subtle"
          >
            {formatDateWithDay(card.expiresAt)} · {formatTime(card.expiresAt)}
          </span>
          <span className="text-xs text-foreground-muted">
            Dismissed at this time if still pending
          </span>
        </div>
      ) : null}
    </div>
  );
});
